import { useId } from 'react';

import { EmptyState } from '@/components/ui';

import { count, percent } from './format';

/**
 * One colour pair per stage, light at the rim and dark at the body's edge:
 * blue while candidates are coming in, warming to orange by the hire.
 */
const STAGE_COLOURS = [
  { light: '#3b82c4', dark: '#1e5a96', rim: '#16487a' },
  { light: '#38b6e0', dark: '#1d8fbd', rim: '#17739a' },
  { light: '#2fc1b0', dark: '#16968a', rim: '#11786f' },
  { light: '#f6c35b', dark: '#e0a12e', rim: '#b98220' },
  { light: '#f5a04a', dark: '#e07b1f', rim: '#b86219' },
  { light: '#ef7d3c', dark: '#cf5a17', rim: '#a64812' },
];

/** Half-widths of each segment's opening, on a 300-unit-wide drawing. */
const TOP = 148;
const BOTTOM = 52;
const SEGMENT_HEIGHT = 60;
const RIM = 7;

/**
 * The hiring funnel drawn as a stacked cone, one segment per stage.
 *
 * The cone's taper is a fixed shape, not a measurement -- the number inside
 * each segment and the shares beside it carry the data. A stage nobody has
 * reached is drawn faded, so an empty stage never looks like a full one.
 */
export function HiringFunnel({ stages = [], emptyLabel = 'No applications yet' }) {
  const id = useId().replace(/:/g, '');
  const top = stages[0]?.count ?? 0;

  if (top === 0) {
    return (
      <EmptyState
        title={emptyLabel}
        description="The funnel fills in as candidates move through your pipeline."
      />
    );
  }

  const steps = stages.length;
  const halfWidth = (index) => TOP - ((TOP - BOTTOM) * index) / steps;

  return (
    <ol className="mx-auto max-w-md">
      {stages.map((stage, index) => {
        const colours = STAGE_COLOURS[Math.min(index, STAGE_COLOURS.length - 1)];
        const t = halfWidth(index);
        // The body narrows to just inside the next segment's rim, leaving the
        // small step that makes the stack read as separate pieces.
        const b = halfWidth(index + 1) - 4;
        const reached = stage.count > 0;
        const gradient = `${id}-stage-${index}`;
        const bottomY = SEGMENT_HEIGHT - RIM;

        return (
          <li
            key={stage.key}
            className="grid grid-cols-[minmax(0,3fr)_minmax(0,2fr)] items-center gap-3"
          >
            <div className="relative" style={{ opacity: reached ? 1 : 0.4 }}>
              <svg
                viewBox={`0 0 300 ${SEGMENT_HEIGHT}`}
                className="block w-full"
                role="img"
                aria-label={`${stage.label}: ${count(stage.count)}`}
              >
                <defs>
                  <linearGradient id={gradient} x1="0" x2="1" y1="0" y2="0">
                    <stop offset="0%" stopColor={colours.dark} />
                    <stop offset="45%" stopColor={colours.light} />
                    <stop offset="100%" stopColor={colours.dark} />
                  </linearGradient>
                </defs>
                <path
                  d={`M ${150 - t} ${RIM} L ${150 - b} ${bottomY} A ${b} ${RIM} 0 0 0 ${150 + b} ${bottomY} L ${150 + t} ${RIM} Z`}
                  fill={`url(#${gradient})`}
                />
                <ellipse cx="150" cy={RIM} rx={t} ry={RIM - 1} fill={colours.rim} />
              </svg>
              <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center pt-2 text-white">
                <span className="text-xs leading-tight font-semibold drop-shadow-sm sm:text-sm">
                  {stage.label}
                </span>
                <span className="text-[10px] leading-tight tabular-nums opacity-90 sm:text-[11px]">
                  {count(stage.count)} {stage.count === 1 ? 'person' : 'people'}
                </span>
              </div>
            </div>

            <div className="flex min-w-0 items-center gap-2 pt-2">
              <span
                aria-hidden="true"
                className="border-line hidden w-6 shrink-0 border-t border-dashed sm:block"
              />
              <span
                aria-hidden="true"
                className="size-2 shrink-0 rounded-full"
                style={{ backgroundColor: colours.dark }}
              />
              <div className="min-w-0 text-xs leading-snug">
                <p className="text-ink font-semibold">
                  {index === 0 ? '100%' : percent(stage.rate_from_top)} of applicants
                </p>
                {index > 0 && (
                  <p className="text-ink-muted">
                    {percent(stage.rate_from_previous)} of previous stage
                  </p>
                )}
              </div>
            </div>
          </li>
        );
      })}
    </ol>
  );
}
